import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { WordSubmission } from '../components/sections/word-submission'
import type { WordSubmissionFormData } from '../types/word-submission'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export function EditWord() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState<any[]>([])
  const [existingWords, setExistingWords] = useState<any[]>([])
  const [wordData, setWordData] = useState<any>(null)
  const [hasAlternatives, setHasAlternatives] = useState(false)

  // Fetch data
  useEffect(() => {
    async function fetchData() {
      if (!user || !id) {
        navigate('/discover')
        return
      }

      setLoading(true)
      try {
        // Fetch word with ownership check
        const { data: wordData, error: wordError } = await supabase
          .from('foreign_words')
          .select(`
            id,
            word,
            word_arabic,
            category_id,
            context,
            reasoning,
            submitted_by,
            alternatives (id)
          `)
          .eq('id', id)
          .single()

        if (wordError) throw wordError

        // Check ownership
        if (wordData.submitted_by !== user.id) {
          toast.error('ليس لديك صلاحية لتعديل هذه الكلمة')
          navigate('/discover')
          return
        }

        // Fetch categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .order('name')

        if (categoriesError) throw categoriesError

        // Fetch existing words for auto-suggest (exclude current word)
        const { data: wordsData, error: wordsError } = await supabase
          .from('foreign_words')
          .select('id, word, word_arabic, category_id, submitted_by, submitted_at')
          .neq('id', id)

        if (wordsError) throw wordsError

        setWordData(wordData)
        setCategories(categoriesData || [])
        setExistingWords(wordsData || [])
        setHasAlternatives(wordData.alternatives?.length > 0)
      } catch (error) {
        console.error('Error fetching data:', error)
        toast.error('حدث خطأ أثناء تحميل البيانات')
        navigate('/discover')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, user, navigate])

  const handleUpdate = async (formData: WordSubmissionFormData) => {
    if (!user || !id) return

    try {
      const { error } = await supabase
        .rpc('update_foreign_word', {
          p_word_id: id,
          p_word: formData.foreignWord,
          p_word_arabic: formData.arabicTransliteration,
          p_category_id: formData.categoryId,
          p_context: formData.usageContext,
          p_reasoning: formData.reasoning,
          p_user_id: user.id,
        })

      if (error) throw error

      toast.success('تم تحديث الكلمة بنجاح!')
      navigate(`/word/${id}/alternatives`)
    } catch (error: any) {
      console.error('Error updating word:', error)
      toast.error(error.message || 'حدث خطأ أثناء تحديث الكلمة')
    }
  }

  // Map data for component
  const mappedCategories = categories.map(cat => ({
    id: cat.id,
    name: cat.name,
    nameEnglish: cat.name_english,
  }))

  const mappedExistingWords = existingWords.map(word => ({
    id: word.id,
    foreignWord: word.word,
    arabicTransliteration: word.word_arabic,
    categoryId: word.category_id,
    submittedBy: word.submitted_by,
    submittedAt: word.submitted_at,
  }))

  const currentUser = user ? {
    id: user.id,
    name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    avatarUrl: user.user_metadata?.avatar_url || null,
  } : undefined

  const initialData = wordData ? {
    foreignWord: wordData.word,
    arabicTransliteration: wordData.word_arabic,
    categoryId: wordData.category_id,
    usageContext: wordData.context,
    reasoning: wordData.reasoning || '',
    egyptianWord: '', // Read-only in edit mode
    explanation: '', // Read-only in edit mode
  } : undefined

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">جاري التحميل...</div>
      </div>
    )
  }

  if (!user || !wordData) {
    return null
  }

  return (
    <WordSubmission
      mode="edit"
      wordId={id}
      initialData={initialData}
      hasAlternatives={hasAlternatives}
      categories={mappedCategories}
      existingWords={mappedExistingWords}
      currentUser={currentUser || { id: '', name: 'Guest', avatarUrl: null }}
      onSubmit={handleUpdate}
      onSearchExisting={() => {}}
    />
  )
}
